import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    addTableReviewItem,
    getTableReview,
    saveTableReviewBulkDecision,
    saveTableReviewDecision,
} from '../../services/api';

const SOURCE_LABELS = {
    auto_hist: 'Auto: HIST',
    auto_stg: 'Auto: STG',
    auto_frequency: 'Auto: Frequency',
    config_source: 'Config: Source',
    config_target: 'Config: Target',
    manual: 'Manual',
};

const SOURCE_FILTERS = [
    { value: 'all', label: 'All' },
    { value: 'auto_hist', label: 'HIST' },
    { value: 'auto_stg', label: 'STG' },
    { value: 'auto_frequency', label: 'Frequency' },
    { value: 'config_source', label: 'Config Source' },
    { value: 'config_target', label: 'Config Target' },
    { value: 'manual', label: 'Manual' },
];

const SOURCE_SORT_ORDER = {
    auto_hist: 0,
    auto_stg: 1,
    auto_frequency: 2,
    config_source: 3,
    config_target: 3,
    manual: 4,
};

const DECISION_COLORS = {
    pending: 'warning',
    accepted: 'success',
    rejected: 'default',
};

function TableReviewDialog({
    open,
    projectId,
    sourceSystem,
    file,
    onClose,
    onUpdated,
}) {
    const [review, setReview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [selectedTable, setSelectedTable] = useState(null);
    const [sourceFilter, setSourceFilter] = useState('all');

    const hasItems = (review?.items || []).length > 0;
    const summary = review?.summary || {};

    const availableOptions = useMemo(
        () => (review?.available_tables || []).map((tableName) => ({ label: tableName, value: tableName })),
        [review]
    );

    const loadReview = async () => {
        if (!open || !projectId || !sourceSystem || !file?.id) {
            return;
        }

        try {
            setLoading(true);
            const result = await getTableReview(projectId, sourceSystem, file.id);
            setReview(result);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to load dropped-table review.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            loadReview();
        } else {
            setReview(null);
            setError(null);
            setSelectedTable(null);
            setSourceFilter('all');
        }
    }, [open, projectId, sourceSystem, file?.id]);

    const sourceCounts = useMemo(() => {
        const counts = {
            auto_hist: 0,
            auto_stg: 0,
            auto_frequency: 0,
            config_source: 0,
            config_target: 0,
            manual: 0,
        };
        for (const item of review?.items || []) {
            if (counts[item.source] !== undefined) {
                counts[item.source] += 1;
            }
        }
        return counts;
    }, [review]);

    const visibleItems = useMemo(() => {
        const items = [...(review?.items || [])];
        items.sort((left, right) => {
            const leftSourceRank = SOURCE_SORT_ORDER[left.source] ?? 99;
            const rightSourceRank = SOURCE_SORT_ORDER[right.source] ?? 99;
            if (leftSourceRank !== rightSourceRank) {
                return leftSourceRank - rightSourceRank;
            }
            return (left.table_name || '').localeCompare((right.table_name || ''), undefined, {
                sensitivity: 'base',
            });
        });
        if (sourceFilter === 'all') {
            return items;
        }
        return items.filter((item) => item.source === sourceFilter);
    }, [review, sourceFilter]);

    const handleDecision = async (tableName, decision) => {
        try {
            setSubmitting(true);
            const result = await saveTableReviewDecision({
                project_id: projectId,
                source_system: sourceSystem,
                file_id: file.id,
                table_name: tableName,
                decision,
            });
            setReview(result);
            setError(null);
            if (onUpdated) {
                onUpdated();
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to save table review decision.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleBulk = async (decision) => {
        try {
            setSubmitting(true);
            const result = await saveTableReviewBulkDecision({
                project_id: projectId,
                source_system: sourceSystem,
                file_id: file.id,
                decision,
            });
            setReview(result);
            setError(null);
            if (onUpdated) {
                onUpdated();
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to apply bulk table review decision.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddTable = async () => {
        if (!selectedTable?.value) {
            return;
        }

        try {
            setSubmitting(true);
            const result = await addTableReviewItem({
                project_id: projectId,
                source_system: sourceSystem,
                file_id: file.id,
                table_name: selectedTable.value,
            });
            setReview(result);
            setSelectedTable(null);
            setError(null);
            if (onUpdated) {
                onUpdated();
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to add table to review.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box>
                    <Typography variant="h6" fontWeight={700}>
                        Dropped Table Review
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {file?.file_name || 'Uploaded file'}
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <Stack spacing={2}>
                        {error && <Alert severity="error">{error}</Alert>}

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip label={`Total ${summary.total || 0}`} variant="outlined" />
                            <Chip label={`Pending ${summary.pending || 0}`} color="warning" variant="outlined" />
                            <Chip label={`Accepted ${summary.accepted || 0}`} color="success" variant="outlined" />
                            <Chip label={`Rejected ${summary.rejected || 0}`} variant="outlined" />
                            <Chip
                                label={summary.can_proceed ? 'Ready to Proceed' : 'Review Required'}
                                color={summary.can_proceed ? 'success' : 'warning'}
                            />
                        </Stack>

                        {!hasItems && (
                            <Alert severity="success">
                                No dropped tables are waiting for review in this file.
                            </Alert>
                        )}

                        {hasItems && (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={() => handleBulk('accepted')}
                                    disabled={submitting}
                                >
                                    Approve All
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="warning"
                                    onClick={() => handleBulk('rejected')}
                                    disabled={submitting}
                                >
                                    Reject All
                                </Button>
                            </Stack>
                        )}

                        {hasItems && (
                            <Box
                                sx={{
                                    p: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                }}
                            >
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                                    Filter redundant tables
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {SOURCE_FILTERS.map((filterOption) => {
                                        const count = filterOption.value === 'all'
                                            ? (review?.items || []).length
                                            : (sourceCounts[filterOption.value] || 0);
                                        return (
                                            <Chip
                                                key={filterOption.value}
                                                label={`${filterOption.label} ${count}`}
                                                color={sourceFilter === filterOption.value ? 'primary' : 'default'}
                                                variant={sourceFilter === filterOption.value ? 'filled' : 'outlined'}
                                                onClick={() => setSourceFilter(filterOption.value)}
                                            />
                                        );
                                    })}
                                </Stack>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Results are sorted by type first, then alphabetically by table name.
                                </Typography>
                            </Box>
                        )}

                        <Box
                            sx={{
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                bgcolor: 'background.default',
                            }}
                        >
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                                Manually add a table to the review list
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <Autocomplete
                                    fullWidth
                                    options={availableOptions}
                                    value={selectedTable}
                                    onChange={(_, nextValue) => setSelectedTable(nextValue)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Available tables" placeholder="Select a table" size="small" />
                                    )}
                                    disabled={submitting}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleAddTable}
                                    disabled={submitting || !selectedTable?.value}
                                >
                                    Add
                                </Button>
                            </Stack>
                        </Box>

                        <Divider />

                        <Stack spacing={1.5}>
                            {visibleItems.map((item) => (
                                <Box
                                    key={`${item.table_name}:${item.source}`}
                                    sx={{
                                        p: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 2,
                                    }}
                                >
                                    <Stack
                                        direction={{ xs: 'column', md: 'row' }}
                                        spacing={1.5}
                                        justifyContent="space-between"
                                        alignItems={{ xs: 'flex-start', md: 'center' }}
                                    >
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography variant="subtitle2" fontWeight={700}>
                                                {item.table_name}
                                            </Typography>
                                            <Stack direction="row" spacing={1} sx={{ mt: 0.75, mb: 0.75 }} flexWrap="wrap" useFlexGap>
                                                <Chip size="small" label={SOURCE_LABELS[item.source] || item.source || 'Review'} variant="outlined" />
                                                <Chip
                                                    size="small"
                                                    label={item.decision || 'pending'}
                                                    color={DECISION_COLORS[item.decision] || 'default'}
                                                />
                                            </Stack>
                                            <Typography variant="body2" color="text.secondary">
                                                {item.reason}
                                            </Typography>
                                        </Box>
                                        <Stack direction="row" spacing={1}>
                                            <Button
                                                variant={item.decision === 'accepted' ? 'contained' : 'outlined'}
                                                color="success"
                                                onClick={() => handleDecision(item.table_name, 'accepted')}
                                                disabled={submitting}
                                            >
                                                Accept
                                            </Button>
                                            <Button
                                                variant={item.decision === 'rejected' ? 'contained' : 'outlined'}
                                                onClick={() => handleDecision(item.table_name, 'rejected')}
                                                disabled={submitting}
                                            >
                                                Reject
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </Box>
                            ))}
                            {hasItems && visibleItems.length === 0 && (
                                <Alert severity="info">
                                    No redundant tables match the selected filter.
                                </Alert>
                            )}
                        </Stack>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default TableReviewDialog;
